// Path Inspector (Analysis > MeshCore > Path Inspector): the payload shape served by GET /api/paths/inspect and the
// pure logic the tab renders from it. NO imports on purpose -- compiled on its own and covered by node:test
// (frontend/tests/pathInspector.test.mjs), so every rule that decides what text the page shows lives here, not in JSX.
//
// Nothing here resolves a hop: the server names the kind of each hop (one node, several, none, impossible) and this
// only words it. A hop that is not one node is always shown as such, never as a guess.

export type InspectMode = "prefix" | "pair";
export const INSPECT_MODES: { id: InspectMode; label: string }[] = [
  { id: "prefix", label: "Prefix path" },
  { id: "pair", label: "Between two nodes" },
];

export type InspectWindow = "24h" | "7d";
export const INSPECT_WINDOWS: InspectWindow[] = ["24h", "7d"];

export const MAX_HOPS = 16;

export const INSPECT_TITLE = "Path Inspector";
export const INSPECT_DESC =
  "Look up a path of hop prefixes, or two nodes, against the routes we have actually heard in the last 24 hours or 7 days, " +
  "grouped by the stretch you asked about. A hop that matches several nodes stays unclear and lists them all. Nothing is guessed.";
export const PREFIX_HELP =
  "Hop prefixes in hex, 1 to 3 bytes each, in the order the packet travelled. Mixed widths are fine. Example: 4b,a2,7c or 4b54 a21f.";
export const PAIR_HELP =
  "A packet counts when the first node (From) relayed it and the second node (To) relayed it later, or is the station that heard it. " +
  "Each group is the stretch from one to the other. The sender of a packet is not part of its path, so a node is only found when it relayed.";
export const NO_ROUTES_TEXT = "We have not heard any route that contains this in the window.";
export const NO_EXAMPLE_TEXT = "There is no recent path to suggest yet.";
export const PENDING_TEXT = "The observation index is still being built. Try again in a few minutes.";

// --- input -------------------------------------------------------------------------------------------------------

export type ParsedPath = { hops: string[]; error?: undefined } | { hops?: undefined; error: string };

const SEPARATORS = /[\s,;:>/|\-→]+/;

/** `4b,a2,7c`, `4b54 a21f` or `0x4b > a2` to lowercase hops; the same rules and wording as the server's parser. */
export function parsePrefixPath(text: string): ParsedPath {
  const tokens = text.split(SEPARATORS).filter((t) => t !== "");
  if (tokens.length === 0) return { error: "Enter a path of hop prefixes, for example 4b,a2,7c." };
  const hops: string[] = [];
  for (const tok of tokens) {
    const h = tok.slice(0, 2).toLowerCase() === "0x" ? tok.slice(2) : tok;
    const bad = [...h].find((c) => !/[0-9a-fA-F]/.test(c));
    if (h === "" || bad !== undefined) return { error: `'${tok}' is not hex: '${bad ?? tok}' is not 0-9 or a-f.` };
    if (h.length % 2 === 1) return { error: `'${tok}' has an odd number of hex digits (${h.length}). A hop is whole bytes, 2 digits each.` };
    if (h.length > 6) return { error: `'${tok}' is ${h.length / 2} bytes. A hop is 1, 2 or 3 bytes (2, 4 or 6 hex digits).` };
    hops.push(h.toLowerCase());
  }
  if (hops.length > MAX_HOPS) return { error: `That is ${hops.length} hops. The most this tool takes is ${MAX_HOPS}.` };
  return { hops };
}

/** The request path for one search. Returns null when there is nothing to ask yet (a blank box). */
export function buildQuery(mode: InspectMode, window: InspectWindow, a: string, b: string): string | null {
  const enc = encodeURIComponent;
  if (mode === "prefix") {
    if (a.trim() === "") return null;
    return `/api/paths/inspect?mode=prefix&window=${window}&path=${enc(a.trim())}`;
  }
  if (a.trim() === "" || b.trim() === "") return null;
  return `/api/paths/inspect?mode=pair&window=${window}&source=${enc(a.trim())}&destination=${enc(b.trim())}`;
}

/** One plain line for a failed request: the server's own wording for bad input, a sentence for the rest. */
export function errorText(status: number | undefined, message: string): string {
  if (status === 401) return "Your session ended. Sign in again to use the Path Inspector.";
  if (status === 403) return "Your account can't use this tool.";
  if (status === 404) return "The Path Inspector isn't available on this server.";
  if (status === 422 || status === 400) {
    const i = message.indexOf(" — ");
    const detail = i >= 0 ? message.slice(i + 3).trim() : "";
    return detail || "That could not be looked up.";
  }
  if (status === undefined) return "Couldn't reach the server. Try again.";
  return "The lookup failed. Try again.";
}

// --- payload -----------------------------------------------------------------------------------------------------

export type HopKind = "unique" | "path" | "ambiguous" | "none" | "impossible" | "observer";

export interface InspectNode {
  id: string;
  name: string | null;
  role: string | null;
  can_relay: boolean;
  last_heard: string | null;
  has_position: boolean;
  lat: number | null;
  lon: number | null;
}

export interface Candidate extends InspectNode {
  /** Still possible once the rest of the path is taken into account. */
  remaining: boolean;
}

export interface InputHop {
  hex: string;
  width: number;
  kind: HopKind;
  node_id: string | null;
  /** Nodes whose id starts with this prefix. */
  n_candidates: number;
  /** Of those, how many are still possible given the rest of the path. */
  n_remaining: number;
  candidates: Candidate[];
}

export interface RouteHop {
  hex: string;
  kind: HopKind;
  node_id: string | null;
  n: number;
  name: string | null;
  lat: number | null;
  lon: number | null;
  /** Prefix mode: this hop is part of the run that was asked for. */
  matched?: boolean;
  /** Pair mode: the first node (From) or the second node (To) of the search. */
  role?: "from" | "to" | null;
}

export interface RouteStats {
  packets: number;
  receptions: number;
  n_observers: number;
  observers: { name: string | null; n: number }[];
  last_seen: string;
  first_seen: string;
  /** Pair mode: both ends resolved to the node, or a hop is unclear and the node is among its candidates. */
  match?: "resolved" | "possible";
}

/** One heard route in full. */
export interface InspectRoute extends RouteStats {
  hops: RouteHop[];
}

/** The resolved stretch that was asked about (prefix mode: the matched hops; pair mode: from the first node to the second),
 *  with the packets that took it and the full routes they came from. */
export interface InspectGroup extends RouteStats {
  hops: RouteHop[];
  /** How many different full routes this group came from (`routes` lists the busiest). */
  n_routes: number;
  routes: InspectRoute[];
}

/** Anything the map can draw. */
export interface Drawable {
  hops: RouteHop[];
}

export interface PairEnd {
  query: string;
  matches: InspectNode[];
  more: boolean;
  node: InspectNode | null;
}

export interface InspectResponse {
  mode: InspectMode;
  window: InspectWindow;
  since: string;
  index_synced_at?: string | null;
  path_context?: boolean;
  hops: InputHop[];
  groups: InspectGroup[];
  total_groups?: number;
  total_routes?: number;
  total_resolved_groups?: number;
  total_packets?: number;
  truncated?: boolean;
  skipped?: number;
  elapsed_ms: number;
  source?: PairEnd;
  destination?: PairEnd;
  need_choice?: boolean;
}

export type InspectPending = { pending: true; message: string };

export interface ExampleResponse {
  path: string | null;
  packets?: number;
  window?: InspectWindow;
}

export function isPending(r: object): r is InspectPending {
  return (r as InspectPending).pending === true;
}

// --- wording -----------------------------------------------------------------------------------------------------

export const DASH = "—";

export function shortId(id: string): string {
  return id.length > 12 ? `${id.slice(0, 8)}…` : id;
}

export function nodeLabel(n: { name: string | null; id: string }): string {
  return n.name && n.name.trim() !== "" ? n.name : shortId(n.id);
}

const ROLE_LABEL: Record<string, string> = { repeater: "Repeater", room_server: "Room server", companion: "Companion", sensor: "Sensor", observer: "Observer", other: "Unknown role" };

export function roleText(role: string | null): string {
  return role === null ? "Unknown role" : ROLE_LABEL[role] ?? "Unknown role";
}

/** The one-line state of an entered hop. */
export function hopStatus(h: InputHop): string {
  switch (h.kind) {
    case "unique":
      return "One node";
    case "path":
      return `One node, the rest of the path settles it (${h.n_candidates} share this prefix)`;
    case "ambiguous":
      return h.n_remaining === h.n_candidates
        ? `Unclear: ${h.n_candidates} nodes share this prefix`
        : `Unclear: ${h.n_remaining} of ${h.n_candidates} nodes are still possible`;
    case "impossible":
      return `Impossible: no consistent answer for this hop on this path (${h.n_candidates} share the prefix)`;
    default:
      return "Unknown: no node we know starts with this";
  }
}

export function hopStatusShort(kind: HopKind): string {
  return kind === "unique" || kind === "path" ? "resolved" : kind === "ambiguous" ? "unclear" : kind === "impossible" ? "impossible" : "unknown";
}

/** A hop in a route chain: the node's name, or the prefix with what is known about it. Never a guessed name. */
export function routeHopLabel(h: RouteHop): string {
  switch (h.kind) {
    case "unique":
    case "path":
    case "observer":
      return nodeLabel({ name: h.name, id: h.node_id ?? h.hex });
    case "ambiguous":
      return `${h.hex} (${h.n} possible)`;
    case "impossible":
      return `${h.hex} (impossible)`;
    default:
      return `${h.hex} (unknown)`;
  }
}

/** The route as one line of text (also the accessible name of the row). */
export function routeLine(r: Drawable): string {
  return r.hops.map(routeHopLabel).join(" → ");
}

export function packetsText(n: number): string {
  return `${n.toLocaleString("en-US")} ${n === 1 ? "packet" : "packets"}`;
}

export function receptionsText(n: number, observers: number): string {
  return `${n.toLocaleString("en-US")} ${n === 1 ? "reception" : "receptions"} by ${observers} ${observers === 1 ? "station" : "stations"}`;
}

/** `2026-10-18T19:47:05.000Z` to `2026-10-18 19:47 UTC`. */
export function seenText(iso: string | null | undefined): string {
  if (!iso) return DASH;
  const m = /^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2})/.exec(iso);
  return m ? `${m[1]} ${m[2]} UTC` : iso;
}

export function summaryText(r: InspectResponse): string {
  const total = r.total_groups ?? r.groups.length;
  const pk = r.total_packets ?? 0;
  const shown = r.groups.length;
  const head = `${total.toLocaleString("en-US")} ${total === 1 ? "group" : "groups"}, ${packetsText(pk)}, last ${r.window === "24h" ? "24 hours" : "7 days"}`;
  const tail = shown < total ? `. Showing the top ${shown}` : "";
  return head + tail;
}

/** The label of the button that lists the full routes under a group. */
export function routesButtonText(g: InspectGroup, open: boolean): string {
  const n = g.n_routes;
  const what = `${n.toLocaleString("en-US")} ${n === 1 ? "route" : "routes"}`;
  return open ? `Hide ${what}` : `Show ${what}`;
}

/** The note under an open group's route list when it holds fewer routes than the group came from. */
export function routesShownText(g: InspectGroup): string | null {
  return g.routes.length < g.n_routes ? `Showing the ${g.routes.length} busiest of ${g.n_routes.toLocaleString("en-US")} routes.` : null;
}

/** The tag next to the first and last node of a pair group. */
export function roleTag(role: "from" | "to" | null | undefined): string | null {
  return role === "from" ? "From" : role === "to" ? "To" : null;
}

export function truncatedText(r: InspectResponse): string | null {
  return r.truncated ? "This matched a very large number of routes. Only the most-heard ones were looked at; enter more hops to narrow it." : null;
}

// --- the map -----------------------------------------------------------------------------------------------------

export interface ChainHopLike {
  index: number;
  pos: { lat: number; lon: number } | null;
}

/**
 * The hops of one route as the map's chain: a hop resolved to a node with a position is drawn at it; an unclear, unknown
 * or impossible hop, or a node with no position, has no position, so the map bridges it with a dashed line to the next
 * drawn hop (the Live Map's rule). Nothing is placed by guesswork.
 */
export function mapHops(r: Drawable): ChainHopLike[] {
  return r.hops.map((h, index) => ({
    index,
    pos: h.node_id !== null && h.lat !== null && h.lon !== null ? { lat: h.lat, lon: h.lon } : null,
  }));
}

export function drawnCount(r: Drawable): number {
  return mapHops(r).filter((h) => h.pos !== null).length;
}

export function mapNote(r: Drawable): string {
  const total = r.hops.length;
  const drawn = drawnCount(r);
  if (drawn === 0) return "None of these hops has a known position, so nothing can be drawn.";
  if (drawn === total) return "Every hop is drawn.";
  return `${drawn} of ${total} hops are drawn. A dashed line passes over hops that are unclear, unknown or have no position.`;
}

// --- pair search -------------------------------------------------------------------------------------------------

export function needsChoice(r: InspectResponse): boolean {
  return r.mode === "pair" && r.need_choice === true;
}

/** Why a chosen node can find nothing: a node that never relays does not appear in paths. */
export function relayNote(n: InspectNode | null): string | null {
  return n !== null && !n.can_relay ? `${nodeLabel(n)} is a ${roleText(n.role).toLowerCase()}, which does not relay. Paths only list relaying nodes.` : null;
}
